<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useToast} from "vue-toast-notification";
import {useAdminStore} from "@/store/admin";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import FormInput from "@/components/ui/FormInput.vue";

const adminStore = useAdminStore();
const toast = useToast();
const drafts = reactive({});
const settings = computed(() => adminStore.systemSettings);
const openGroups = ref([]);

const categoryLabels = {
    coins_limit: "Ограничения монет",
	orders_limit: "Ограничения заказов",
    delivery: "Доставка",
    file_size: "Размер файлов",
    backup: "Бэкап",
};

const settingGroups = computed(() => {
    const groups = new Map();
    settings.value.forEach(item => {
        const category = item.category || "other";
        if (!groups.has(category)) {
            groups.set(category, {
                name: category,
                label: categoryLabels[category] || category,
                items: [],
            });
        }
        groups.get(category).items.push(item);
    });
    return [...groups.values()];
});

function syncDrafts(items) {
    items.forEach(item => {
        drafts[item.name] = item.value ?? item.effective_value ?? "";
    });
}

function sourceLabel(item) {
    return {
        database: "БД",
        environment: "Окружение",
        default: "Безопасное значение",
    }[item.source] || item.source;
}

async function load() {
    const data = await adminStore.loadSystemSettings();
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    syncDrafts(data);
    const names = settingGroups.value.map(group => group.name);
    const kept = openGroups.value.filter(name => names.includes(name));
    openGroups.value = kept.length ? kept : (names[0] ? [names[0]] : []);
}

function isExpanded(category) {
    return openGroups.value.includes(category);
}

function toggleCategory(category) {
    openGroups.value = isExpanded(category)
        ? openGroups.value.filter(name => name !== category)
        : [...openGroups.value, category];
}

function expandAll() {
    openGroups.value = settingGroups.value.map(group => group.name);
}

function collapseAll() {
    openGroups.value = [];
}

async function save(item) {
    const value = String(drafts[item.name] ?? "").trim();
    if (!value) {
        toast.error("Введите целое значение или сбросьте переопределение");
        return;
    }
    const updated = await adminStore.updateSystemSetting(item.name, value);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (updated) {
        drafts[item.name] = updated.value ?? updated.effective_value;
        toast.success("Настройка сохранена");
    }
}

async function reset(item) {
    const updated = await adminStore.updateSystemSetting(item.name, null);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (updated) {
        drafts[item.name] = updated.effective_value;
        toast.success("Переопределение сброшено");
    }
}

onMounted(load);

</script>

<template>
  <div class="systemSettings">
    <MainPreloader v-if="adminStore.loading" position="fixed" :size="80" :stroke="3"/>

    <div class="systemSettings__heading">
      <div>
        <h2>Системные настройки</h2>
        <p>Изменения применяются к новым запросам без перезапуска сервера.</p>
      </div>
      <div class="systemSettings__actions">
        <ActionButton title="Раскрыть все" @buttonClick="expandAll">
          <template #caption>+</template>
        </ActionButton>
        <ActionButton title="Свернуть все" @buttonClick="collapseAll">
          <template #caption>-</template>
        </ActionButton>
        <ActionButton @buttonClick="load">
          <template #caption>Обновить</template>
        </ActionButton>
      </div>
    </div>

    <EmptyCard v-if="!settings.length && !adminStore.loading"/>

    <div v-else class="systemSettings__list">
      <section v-for="group in settingGroups" :key="group.name" class="systemSettings__group">
        <button
            class="systemSettings__groupButton"
            type="button"
            :aria-controls="`system-settings-${group.name}`"
            :aria-expanded="isExpanded(group.name)"
            @click="toggleCategory(group.name)"
        >
          <span>{{ group.label }}</span>
          <span class="systemSettings__groupCount">{{ group.items.length }}</span>
          <span class="systemSettings__groupChevron" :class="{ 'systemSettings__groupChevron--open': isExpanded(group.name) }"/>
        </button>
        <div v-if="isExpanded(group.name)" :id="`system-settings-${group.name}`" class="systemSettings__groupContent">
          <article v-for="item in group.items" :key="item.name" class="systemSettings__item">
            <div class="systemSettings__details">
              <h3>{{ item.description }}</h3>
              <code>{{ item.name }}</code>
              <div class="systemSettings__meta">
                <span>Диапазон: {{ item.min_value }}–{{ item.max_value }}</span>
                <span>Источник: <strong>{{ sourceLabel(item) }}</strong></span>
                <span>Действует: <strong>{{ item.effective_value }}</strong></span>
              </div>
            </div>
            <div class="systemSettings__controls">
              <FormInput v-model="drafts[item.name]" type="number"/>
              <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="save(item)">
                <template #caption>Сохранить</template>
              </ActionButton>
              <button
                  class="systemSettings__reset"
                  :disabled="item.value === null"
                  @click="reset(item)"
              >
                Сбросить
              </button>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped lang="scss">
.systemSettings {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;

    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      color: var(--cd-text);
    }

    p {
      margin: 6px 0 0;
      color: var(--cd-text-muted);
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__group {
    display: flex;
    flex-direction: column;
  }

  &__groupButton {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 6px 16px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--cd-text);
    cursor: pointer;
    font: inherit;
    font-weight: 600;
    text-align: left;
    transition: background .2s ease;

    &[aria-expanded="false"]:hover {
      background: rgba(111, 192, 98, .08);
    }

    &:focus-visible {
      outline: 2px solid var(--cd-base-green);
      outline-offset: -2px;
      border-radius: 8px;
    }
  }

  &__groupCount {
    margin-left: 8px;
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    text-align: center;
  }

  &__groupChevron {
    width: 9px;
    height: 9px;
    margin-left: auto;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg) translateY(-3px);
    transition: transform .2s ease;

    &--open {
      transform: rotate(225deg) translateY(-3px);
    }
  }

  &__groupContent {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__item {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(360px, 46%);
    align-items: center;
    gap: 24px;
    padding: 16px;
    border-radius: 12px;
    background: var(--cd-card-bg);

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__details h3 {
    margin: 0 0 8px;
    font-size: 16px;
  }

  &__details code {
    color: var(--cd-base-green);
    font-size: 13px;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-top: 12px;
    color: var(--cd-text-muted);
    font-size: 13px;

    strong { color: var(--cd-text); }
  }

  &__controls {
    display: grid;
    grid-template-columns: minmax(110px, 1fr) auto auto;
    align-items: center;
    gap: 8px;

    @include respond-to('sm') {
      grid-template-columns: 1fr 1fr;

      .formInputWrapper { grid-column: 1 / -1; }
    }
  }

  &__reset {
    min-height: 40px;
    padding: 0 14px;
    border: 1px solid var(--cd-field-border);
    border-radius: 12px;
    background: transparent;
    color: var(--cd-text-muted);
    cursor: pointer;

    &:disabled {
      cursor: default;
      opacity: .5;
    }
  }
}
</style>
